import { Component, inject, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { FilterService } from '../../../../../_shared/services';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DashboardService } from '../../../services/dashboard.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ScheduleAndAssignModalComponent } from '../../../../maintenance/work-order/components/modals/schedule-and-assign-modal/schedule-and-assign-modal.component';
import { WorkOrderItem } from '../../../../maintenance/work-order/models/work-orders-overview.models';
import { UpcomingActivityRequest, UpcomingActivityResponse } from '../../../models/dashboard.model';
import { AssetType } from '../../../../_shared/enums/asset-type.enum';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-upcoming-activity-widget',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent, InfiniteScrollModule, TranslocoPipe],
  templateUrl: './upcoming-activity-widget.component.html',
  styleUrl: './upcoming-activity-widget.component.scss'
})
export class UpcomingActivityWidgetComponent implements OnInit, OnChanges, OnDestroy {
  @Input() assetTypes: AssetType[] = [];

  private filterService = inject(FilterService);
  private dashboardService = inject(DashboardService);
  private errorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  title: string = 'maintenanceDashboard.upcomingActivity';
  isLoading: boolean = true;
  widgetFilter!: WidgetFilterData;

  activities: UpcomingActivityResponse[] = [];
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;

  private cancelSubject: Subject<void> = new Subject<void>();

  constructor() {
    this.filterService.filterUpdated$.subscribe(async (filter) => {
      if (Object.values(filter).every(x => x === null) || filter === null) return;

      this.widgetFilter = filter;
      this.resetPagination();
      await this.loadUpcomingActivityData();
    });
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;

    if (this.widgetFilter && !Object.values(this.widgetFilter).every(x => x === null)) {
      this.loadUpcomingActivityData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['assetTypes'] || changes['assetTypes'].firstChange || !this.widgetFilter) {
      return;
    }

    this.resetPagination();
    this.loadUpcomingActivityData();
  }

  ngOnDestroy(): void {
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onScrollDown(): void {
    if (this.isLoading || this.page >= this.totalPages) return;

    this.page++;
    this.loadUpcomingActivityData();
  }

  private resetPagination(): void {
    this.cancelSubject.next();
    this.page = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.activities = [];
  }

  private async loadUpcomingActivityData(): Promise<void> {
    this.isLoading = true;

    try {
      const request: UpcomingActivityRequest = {
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        interval: this.widgetFilter.interval,
        page: this.page,
        pageSize: this.pageSize,
        assetTypes: this.assetTypes
      };

      const response = await this.dashboardService.getUpcomingActivities(request, this.cancelSubject);
      const pagination = response.data;
      const newActivities = pagination?.results ?? [];

      this.activities = this.page === 1
        ? newActivities
        : [...this.activities, ...newActivities];
      this.totalPages = pagination?.totalPages ?? 0;
      this.totalCount = pagination?.totalCount ?? 0;

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          if (this.page === 1) {
            this.activities = [];
          }
          this.isLoading = false;
        }
      });
    }
  }

  onSeeMore(): void {
    this.router.navigate(['/asset-management/work-order']);
  }

  onActivityClick(): void {
    this.router.navigate(['/asset-management/work-order']);
  }

  onAssign(activity: UpcomingActivityResponse, event: Event): void {
    event.stopPropagation();

    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'schedule-and-assign-modal'
    };

    const modalRef = this.modalService.open(ScheduleAndAssignModalComponent, modalOptions);
    modalRef.componentInstance.workOrderId = activity.workOrderId;
    modalRef.componentInstance.workOrderData = {
      workOrderId: activity.workOrderId,
      workOrderNumber: activity.workOrderNumber,
      assetName: activity.assetName,
      reason: activity.maintenanceReason,
      scheduledDate: activity.scheduledDate ?? ''
    } as WorkOrderItem;

    modalRef.result.then(
      (result) => {
        if (result) {
          this.resetPagination();
          this.loadUpcomingActivityData();
        }
      },
      () => { }
    );
  }

  getMaintenanceTypeClass(type: string): string {
    const typeMap: { [key: string]: string } = {
      'Routine Maintenance': 'maintenance-type--routine',
      'Emergency Maintenance': 'maintenance-type--emergency',
      'Corrective Maintenance': 'maintenance-type--corrective',
      'Preventative Maintenance': 'maintenance-type--preventative'
    };
    return typeMap[type] || 'maintenance-type--routine';
  }
}